import { useRef } from 'react';
import cx from 'classnames';
import './SegmentedControl.css';

/**
 * QHS SegmentedControl
 * --------------------
 * iOS/macOS-style segmented switcher. Renders a tinted track with
 * an active pill that floats above the inactive segments. Use
 * for short, mutually-exclusive choices in forms (gender,
 * yes/no/unknown allergy state, currency switcher, etc.) — when
 * a select would feel too heavy and a radio list would feel too
 * sparse.
 *
 *   <SegmentedControl
 *     value={values.genderAtBirth}
 *     onChange={(v) => setFieldValue('genderAtBirth', v)}
 *     options={[
 *       { value: 'female', label: 'Female' },
 *       { value: 'male', label: 'Male' },
 *     ]}
 *   />
 *
 * Optional `fullWidth` makes the track stretch to its container.
 * `disabled` greys out all options. `error` adds a red border so
 * the control matches the rest of the form's validation language.
 *
 * Keyboard: Tab moves into the group. Arrow keys cycle between
 * segments. Enter / Space select the focused segment.
 */
const SegmentedControl = ({
  value,
  onChange,
  options = [],
  fullWidth = false,
  disabled = false,
  error = false,
  className,
  ...rest
}) => {
  const refs = useRef([]);

  const focusOption = (index) => {
    const target = refs.current[index];
    if (target) target.focus();
  };

  const handleKeyDown = (event, index) => {
    const last = options.length - 1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(index === last ? 0 : index + 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusOption(index === 0 ? last : index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusOption(last);
    }
  };

  return (
    <div
      role='radiogroup'
      className={cx(
        'qhs-seg',
        fullWidth && 'qhs-seg--full',
        error && 'qhs-seg--invalid',
        className,
      )}
      {...rest}
    >
      {options.map((option, index) => {
        const isActive = option.value === value;
        return (
          <button
            key={String(option.value)}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type='button'
            role='radio'
            aria-checked={isActive}
            tabIndex={isActive || (value === undefined && index === 0) ? 0 : -1}
            disabled={disabled || option.disabled}
            onClick={() => !disabled && !option.disabled && onChange?.(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cx('qhs-seg__option', isActive && 'qhs-seg__option--active')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
